import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import colors from "@constants/colors"
import animations from "@constants/animations"
import { Chevron } from "@components/universal/icons"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"

const Header = styled.div`
  flex-direction: row;
  align-items: center;
  align-self: flex-end;
  justify-content: flex-end;
  display: flex;
  display: -webkit-flex;
  transition: width 200ms ${animations.bezier};
  padding: 0 24px;
  border: solid 1px transparent;
  background-color: transparent;
  &:hover {
    cursor: pointer;
  }
  height: 40px;
  width: 100%;
`

const HelperText = styled.div`
  right: 52px;
  opacity: 0;
  transition: opacity 200ms ${animations.bezier};
  line-height: 22px;
  height: 100%;
  align-items: center;
  color: ${colors.dark};
  pointer-events: none;
`

const Value = styled.div`
  opacity: 1;
  transition: opacity 200ms ${animations.bezier};
  margin-right: 14px;
  line-height: 22px;
  height="100%
   align-items: center;
  color: ${colors.dark};
  pointer-events: none;
`

const IconWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  transform: rotate(90deg);
  transition: transform 200ms ${animations.bezier};
  width: 22px;
  height: 22px;
  pointer-events: none;
`

const Options = styled.div`
  z-index: 1;
  opacity: 0;
  transition: opacity 200ms ${animations.bezier};
  width: 100%;
  padding: 24px 0;
  background: ${colors.white};
  box-shadow: 0px 4px 40px ${colors.divider};
  border-radius: 6px;
  position: absolute;
  top: 50px;
  right: 0px;
  min-width: 240px;
`

const Option = styled.div<{ selected: boolean }>`
  transition: background-color 200ms ${animations.bezier};
  padding: 0 34px;
  line-height: 34px;
  cursor: pointer;
  color: ${colors.midAccessible};
  background-color: ${colors.white};

  &:hover {
    background-color: ${colors.lightest};
  }
`

export const Container = styled.div<{ open: boolean }>`
  ${({ open }) => css`
    position: relative;
    flex-direction: column;
    align-items: center;
    display: flex;
    width: 100%;
    height: 100%
    outline: none;
    overflow: visible;
    border-bottom: none;
    &:focus{
      outline: none;
    }

    ${
      open &&
      css`
        .Dropdown__Header {
          width: 100%;
        }

        .Dropdown__Header__HelperText {
          position: absolute;
          right: 60px;
          opacity: 1;
          line-height: 44px;
        }

        .Dropdown__Header__Value {
          opacity: 0;
        }

        .Dropdown__Header__IconWrapper {
          transform: rotate(270deg);
        }

        .Dropdown__Options {
          opacity: 1;
          border-top: none;
        }
      `
    }
  `}
`

interface DropdownProps {
  helperText: string
  value: string
  options: Array<{ label: string; value: string }>
  onChange: (string) => void
}

const Dropdown: React.FC<DropdownProps> = ({
  helperText,
  value,
  options,
  onChange,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)

  const [open, setOpen] = React.useState<boolean>(false)

  const toggleOpen = () => {
    setOpen(!open)
  }

  const closeDropdown = () => {
    setOpen(false)
  }

  useModalBackgroundClose({
    modalRef: containerRef,
    isOpen: open,
    onRequestClose: closeDropdown,
    allowScroll: true,
  })

  const getHandlerForOption = (option) => {
    return React.useCallback(() => {
      onChange(option)
      closeDropdown()
    }, [option, onChange])
  }

  const optionForValue = options.find((option) => option.value === value)

  return (
    <Container
      className="Dropdown__Container"
      ref={containerRef}
      tabIndex={0}
      open={open}
    >
      <Header className="Dropdown__Header" onClick={toggleOpen}>
        {open && (
          <HelperText className="Dropdown__Header__HelperText">
            {helperText}
          </HelperText>
        )}
        <Value className="Dropdown__Header__Value">
          {optionForValue && "Sort By: " + optionForValue.label}
        </Value>
        <IconWrapper className="Dropdown__Header__IconWrapper">
          <Chevron />
        </IconWrapper>
      </Header>
      <Options className="Dropdown__Options">
        {options.map((option) => (
          <Option
            key={option.label}
            selected={option.value === value}
            onClick={getHandlerForOption(option)}
          >
            {option.label}
          </Option>
        ))}
      </Options>
    </Container>
  )
}

export default Dropdown
